import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiCalendar, FiInfo, FiTrophy, FiTarget, FiUser, FiFilter, FiClock, FiX, FiCheck, FiUsers, FiAlertTriangle, FiMapPin } = FiIcons;

// Added team logos
const teamLogos = {
  'Manchester City': '/public/team-logos/man-city.png',
  'Arsenal': '/public/team-logos/arsenal.png',
  'Liverpool': '/public/team-logos/liverpool.png',
  'Chelsea': '/public/team-logos/chelsea.png',
  'Manchester United': '/public/team-logos/man-utd.png',
  'Tottenham': '/public/team-logos/tottenham.png',
  'West Ham United': '/public/team-logos/west-ham.png',
  'Everton': '/public/team-logos/everton.png',
  'Newcastle United': '/public/team-logos/newcastle.png',
  'Brentford': '/public/team-logos/brentford.png',
  'Brighton': '/public/team-logos/brighton.png',
  'Burnley': '/public/team-logos/burnley.png',
  'Nottingham Forest': '/public/team-logos/nottingham.png',
  'Bournemouth': '/public/team-logos/bournemouth.png',
  'Aston Villa': '/public/team-logos/aston-villa.png',
  'Crystal Palace': '/public/team-logos/crystal-palace.png',
  'Wolverhampton': '/public/team-logos/wolves.png',
  'Fulham': '/public/team-logos/fulham.png',
  'Luton Town': '/public/team-logos/luton.png',
  'Sheffield United': '/public/team-logos/sheffield.png',
  'Real Madrid': '/public/team-logos/real-madrid.png',
  'Barcelona': '/public/team-logos/barcelona.png',
  'Atletico Madrid': '/public/team-logos/atletico.png',
  'Sevilla': '/public/team-logos/sevilla.png',
  'Valencia': '/public/team-logos/valencia.png',
  'Athletic Bilbao': '/public/team-logos/athletic-bilbao.png',
  'Real Sociedad': '/public/team-logos/real-sociedad.png',
  'Villarreal': '/public/team-logos/villarreal.png',
  'Betis': '/public/team-logos/betis.png',
  'Real Mallorca': '/public/team-logos/mallorca.png',
  'Getafe': '/public/team-logos/getafe.png',
  'Osasuna': '/public/team-logos/osasuna.png',
  'Cadiz': '/public/team-logos/cadiz.png',
  'Alaves': '/public/team-logos/alaves.png',
  'Rayo Vallecano': '/public/team-logos/rayo.png',
  'Las Palmas': '/public/team-logos/las-palmas.png',
  'Celta Vigo': '/public/team-logos/celta.png',
  'Girona': '/public/team-logos/girona.png',
  'Almeria': '/public/team-logos/almeria.png',
  'Granada': '/public/team-logos/granada.png',
  // International teams 
  'Brazil': '/public/team-logos/brazil.png',
  'France': '/public/team-logos/france.png',
  'Argentina': '/public/team-logos/argentina.png',
  'Germany': '/public/team-logos/germany.png',
  'Italy': '/public/team-logos/italy.png',
  'England': '/public/team-logos/england.png',
  'Spain': '/public/team-logos/spain.png',
  'Portugal': '/public/team-logos/portugal.png',
  'Netherlands': '/public/team-logos/netherlands.png',
  'Belgium': '/public/team-logos/belgium.png',
  'Croatia': '/public/team-logos/croatia.png',
  'Uruguay': '/public/team-logos/uruguay.png',
  'Denmark': '/public/team-logos/denmark.png',
  'Switzerland': '/public/team-logos/switzerland.png',
  'Poland': '/public/team-logos/poland.png',
  'USA': '/public/team-logos/usa.png',
  'Mexico': '/public/team-logos/mexico.png',
  'Japan': '/public/team-logos/japan.png',
  'Senegal': '/public/team-logos/senegal.png',
  'Morocco': '/public/team-logos/morocco.png',
};

// Fallback logo icon for teams without a specific logo
const getTeamLogo = (teamName) => {
  const defaultLogo = (
    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-bull-gray rounded-full flex items-center justify-center text-bull-white text-xs">
      {teamName.charAt(0)}
    </div>
  );

  if (teamLogos[teamName]) {
    return (
      <img 
        src={teamLogos[teamName]} 
        alt={`${teamName} logo`} 
        className="w-4 h-4 sm:w-5 sm:h-5 object-contain" 
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }} 
      />
    );
  }
  return defaultLogo;
};

// Enhanced matchweek data with more leagues and competitions (removed importance field)
const matchweekData = {
  'premier-league': {
    name: 'Premier League',
    country: 'England',
    level: 'Domestic',
    matchweeks: {
      36: {
        name: 'Matchweek 36',
        matches: [
          { id: 1, home: 'Manchester City', away: 'West Ham United', date: '2024-05-12', time: '16:00' },
          { id: 2, home: 'Arsenal', away: 'Everton', date: '2024-05-12', time: '16:00' },
          { id: 3, home: 'Brentford', away: 'Newcastle United', date: '2024-05-12', time: '16:00' },
          { id: 4, home: 'Brighton', away: 'Manchester United', date: '2024-05-12', time: '16:00' },
          { id: 5, home: 'Burnley', away: 'Nottingham Forest', date: '2024-05-12', time: '16:00' },
          { id: 6, home: 'Chelsea', away: 'Bournemouth', date: '2024-05-12', time: '16:00' },
          { id: 7, home: 'Crystal Palace', away: 'Aston Villa', date: '2024-05-12', time: '16:00' },
          { id: 8, home: 'Liverpool', away: 'Wolverhampton', date: '2024-05-12', time: '16:00' },
          { id: 9, home: 'Luton Town', away: 'Fulham', date: '2024-05-12', time: '16:00' },
          { id: 10, home: 'Tottenham', away: 'Sheffield United', date: '2024-05-12', time: '16:00' }
        ]
      },
      37: {
        name: 'Matchweek 37',
        matches: [
          { id: 11, home: 'West Ham United', away: 'Manchester City', date: '2024-05-15', time: '19:00' },
          { id: 12, home: 'Everton', away: 'Arsenal', date: '2024-05-15', time: '19:00' },
          { id: 13, home: 'Newcastle United', away: 'Brentford', date: '2024-05-15', time: '19:00' },
          { id: 14, home: 'Manchester United', away: 'Brighton', date: '2024-05-15', time: '19:00' },
          { id: 15, home: 'Nottingham Forest', away: 'Burnley', date: '2024-05-15', time: '19:00' },
          { id: 16, home: 'Bournemouth', away: 'Chelsea', date: '2024-05-15', time: '19:00' },
          { id: 17, home: 'Aston Villa', away: 'Crystal Palace', date: '2024-05-15', time: '19:00' },
          { id: 18, home: 'Wolverhampton', away: 'Liverpool', date: '2024-05-15', time: '19:00' },
          { id: 19, home: 'Fulham', away: 'Luton Town', date: '2024-05-15', time: '19:00' },
          { id: 20, home: 'Sheffield United', away: 'Tottenham', date: '2024-05-15', time: '19:00' }
        ]
      }
    }
  },
  'la-liga': {
    name: 'La Liga',
    country: 'Spain',
    level: 'Domestic',
    matchweeks: {
      34: {
        name: 'Matchweek 34',
        matches: [
          { id: 31, home: 'Real Madrid', away: 'Barcelona', date: '2024-04-18', time: '21:00' },
          { id: 32, home: 'Atletico Madrid', away: 'Sevilla', date: '2024-04-18', time: '19:00' },
          { id: 33, home: 'Valencia', away: 'Athletic Bilbao', date: '2024-04-18', time: '17:00' },
          { id: 34, home: 'Real Sociedad', away: 'Villarreal', date: '2024-04-18', time: '19:00' },
          { id: 35, home: 'Betis', away: 'Real Mallorca', date: '2024-04-18', time: '17:00' },
          { id: 36, home: 'Getafe', away: 'Osasuna', date: '2024-04-18', time: '15:00' },
          { id: 37, home: 'Cadiz', away: 'Alaves', date: '2024-04-18', time: '15:00' },
          { id: 38, home: 'Rayo Vallecano', away: 'Las Palmas', date: '2024-04-18', time: '17:00' },
          { id: 39, home: 'Celta Vigo', away: 'Girona', date: '2024-04-18', time: '19:00' },
          { id: 40, home: 'Almeria', away: 'Granada', date: '2024-04-18', time: '21:00' }
        ]
      }
    }
  },
  'champions-league': {
    name: 'Champions League',
    country: 'Europe',
    level: 'International',
    matchweeks: {
      1: {
        name: 'Quarter Finals',
        matches: [
          { id: 51, home: 'Manchester City', away: 'Real Madrid', date: '2024-04-09', time: '20:00', mustHaveWinner: true },
          { id: 52, home: 'Bayern Munich', away: 'Arsenal', date: '2024-04-09', time: '20:00', mustHaveWinner: true },
          { id: 53, home: 'Barcelona', away: 'PSG', date: '2024-04-10', time: '20:00', mustHaveWinner: true },
          { id: 54, home: 'Atletico Madrid', away: 'Borussia Dortmund', date: '2024-04-10', time: '20:00', mustHaveWinner: true }
        ]
      }
    }
  }
};

// Enhanced individual match data (removed importance field)
const individualMatchData = [
  {
    id: 101,
    home: 'Manchester City',
    away: 'Liverpool',
    competition: 'Premier League',
    date: '2024-05-22',
    time: '20:00',
    venue: 'Etihad Stadium',
    stage: 'League Match',
    country: 'England',
    level: 'Domestic',
    drawAllowed: true,
    extraTimeAllowed: false,
    penaltiesAllowed: false,
    goldenGoalAllowed: false
  },
  {
    id: 102,
    home: 'Barcelona',
    away: 'Real Madrid',
    competition: 'Copa del Rey',
    date: '2024-05-24',
    time: '21:30',
    venue: 'Camp Nou',
    stage: 'Final',
    country: 'Spain',
    level: 'Domestic',
    drawAllowed: false,
    extraTimeAllowed: true,
    penaltiesAllowed: true,
    goldenGoalAllowed: false
  },
  {
    id: 103,
    home: 'Bayern Munich',
    away: 'Borussia Dortmund',
    competition: 'Champions League',
    date: '2024-05-25',
    time: '20:00',
    venue: 'Allianz Arena',
    stage: 'Semi-final',
    country: 'Europe',
    level: 'International',
    drawAllowed: false,
    extraTimeAllowed: true,
    penaltiesAllowed: true,
    goldenGoalAllowed: false
  },
  {
    id: 104,
    home: 'France',
    away: 'Germany',
    competition: 'International Friendly',
    date: '2024-06-01',
    time: '20:00',
    venue: 'Stade de France',
    stage: 'Friendly',
    country: 'Europe',
    level: 'International',
    drawAllowed: true,
    extraTimeAllowed: false,
    penaltiesAllowed: false,
    goldenGoalAllowed: false
  },
  {
    id: 105,
    home: 'England',
    away: 'Spain',
    competition: 'UEFA Nations League',
    date: '2024-06-15',
    time: '20:00',
    venue: 'Wembley Stadium',
    stage: 'Final',
    country: 'Europe',
    level: 'International',
    drawAllowed: false,
    extraTimeAllowed: true,
    penaltiesAllowed: true,
    goldenGoalAllowed: true
  },
  {
    id: 106,
    home: 'Juventus',
    away: 'AC Milan',
    competition: 'Serie A',
    date: '2024-06-20',
    time: '20:45',
    venue: 'Allianz Stadium',
    stage: 'League Match',
    country: 'Italy',
    level: 'Domestic',
    drawAllowed: true,
    extraTimeAllowed: false,
    penaltiesAllowed: false,
    goldenGoalAllowed: false
  }
];

// Enhanced tournament data
const tournamentData = [
  {
    id: 201,
    name: 'UEFA Euro 2024',
    date: '2024-06-14 - 2024-07-14',
    location: 'Germany',
    level: 'International',
    country: 'Europe',
    categories: ['Men\'s National Teams'],
    teams: {
      'Men\'s National Teams': [
        'England', 'France', 'Germany', 'Spain', 'Portugal', 'Belgium', 'Italy', 'Netherlands',
        'Croatia', 'Denmark', 'Switzerland', 'Poland', 'Serbia', 'Ukraine', 'Turkey', 'Austria',
        'Hungary', 'Czech Republic', 'Scotland', 'Slovenia', 'Albania', 'Romania', 'Georgia', 'Slovakia'
      ]
    },
    structure: {
      'Men\'s National Teams': {
        winner: null,
        finalist: null,
        semifinalists: [null, null],
        quarterfinalists: [null, null, null, null]
      }
    }
  },
  {
    id: 202,
    name: 'Copa America 2024',
    date: '2024-06-20 - 2024-07-14',
    location: 'United States',
    level: 'International',
    country: 'Americas',
    categories: ['Men\'s National Teams'],
    teams: {
      'Men\'s National Teams': [
        'Argentina', 'Brazil', 'Uruguay', 'Colombia', 'Chile', 'Peru', 'Paraguay', 'Ecuador',
        'Bolivia', 'Venezuela', 'Mexico', 'USA', 'Canada', 'Jamaica', 'Panama', 'Costa Rica'
      ]
    },
    structure: {
      'Men\'s National Teams': {
        winner: null,
        finalist: null,
        semifinalists: [null, null],
        quarterfinalists: [null, null, null, null]
      }
    }
  },
  {
    id: 203,
    name: 'FIFA World Cup 2026',
    date: '2026-06-11 - 2026-07-19',
    location: 'USA/Canada/Mexico',
    level: 'International',
    country: 'Americas',
    categories: ['Men\'s National Teams'],
    teams: {
      'Men\'s National Teams': [
        'Brazil', 'Argentina', 'France', 'England', 'Spain', 'Germany', 'Portugal', 'Netherlands',
        'Italy', 'Belgium', 'Croatia', 'Uruguay', 'Colombia', 'Mexico', 'USA', 'Japan',
        'Morocco', 'Senegal', 'Australia', 'Denmark', 'Switzerland', 'Poland', 'Ukraine', 'Turkey'
      ]
    },
    structure: {
      'Men\'s National Teams': {
        winner: null,
        finalist: null,
        semifinalists: [null, null],
        quarterfinalists: [null, null, null, null]
      }
    }
  }
];

// Match structure options for no-draw matches
const getMatchStructureOptions = (match) => {
  const options = [
    { key: '2-halves', label: '2 Halves (90 min)', description: 'Regular time only' }
  ];

  if (match.extraTimeAllowed) {
    options.push({ key: 'extra-time', label: 'Extra Time (120 min)', description: '90 min + 30 min extra time' });
  }

  if (match.goldenGoalAllowed) {
    options.push({ key: 'golden-goal', label: 'Golden Goal', description: 'First goal in extra time wins' });
  }

  if (match.penaltiesAllowed) {
    options.push({ key: 'penalties', label: 'Penalties', description: 'Decided by penalty shootout' });
  }

  return options;
};

const FootballCard = ({ onStake }) => {
  const [predictionMode, setPredictionMode] = useState('matchweek');
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [selectedMatchweek, setSelectedMatchweek] = useState('all');
  const [selectedMatch, setSelectedMatch] = useState('all');
  const [selectedTournament, setSelectedTournament] = useState('all');

  // Enhanced filter states
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedDate, setSelectedDate] = useState('all');

  // Score state for matches with 8 or fewer games
  const [matchScores, setMatchScores] = useState({});

  const [isLeagueDropdownOpen, setIsLeagueDropdownOpen] = useState(false);
  const [isMatchweekDropdownOpen, setIsMatchweekDropdownOpen] = useState(false);
  const [isMatchDropdownOpen, setIsMatchDropdownOpen] = useState(false);
  const [isTournamentDropdownOpen, setIsTournamentDropdownOpen] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  const [matchweekPredictions, setMatchweekPredictions] = useState({});
  const [matchPredictions, setMatchPredictions] = useState({});
  const [tournamentPredictions, setTournamentPredictions] = useState({});

  const predictionModes = [
    { key: 'matchweek', label: 'Matchweek Mode', description: 'Predict entire matchweek results' },
    { key: 'match', label: 'Individual Match', description: 'Predict detailed match outcome' },
    { key: 'tournament', label: 'Tournament Mode', description: 'Predict tournament structure' }
  ];

  const leagues = [
    { key: 'all', name: 'All Competitions' },
    { key: 'premier-league', name: 'Premier League' },
    { key: 'la-liga', name: 'La Liga' },
    { key: 'champions-league', name: 'Champions League' }
  ];

  // Enhanced filter options
  const countryOptions = [
    { key: 'all', name: 'All Countries' },
    { key: 'England', name: 'England' },
    { key: 'Spain', name: 'Spain' },
    { key: 'Italy', name: 'Italy' },
    { key: 'Germany', name: 'Germany' },
    { key: 'France', name: 'France' },
    { key: 'Europe', name: 'Europe' },
    { key: 'Americas', name: 'Americas' }
  ];

  const levelOptions = [
    { key: 'all', name: 'All Levels' },
    { key: 'Domestic', name: 'Domestic Leagues' },
    { key: 'International', name: 'International' }
  ];

  const dateOptions = [
    { key: 'all', name: 'All Dates' },
    { key: 'today', name: 'Today' },
    { key: 'tomorrow', name: 'Tomorrow' },
    { key: 'this-week', name: 'This Week' },
    { key: 'next-week', name: 'Next Week' }
  ];

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedLeague('all');
    setSelectedMatchweek('all');
    setSelectedMatch('all');
    setSelectedTournament('all');
    setSelectedCountry('all');
    setSelectedLevel('all');
    setSelectedDate('all');
    setMatchweekPredictions({});
    setMatchPredictions({});
    setTournamentPredictions({});
    setMatchScores({});
  };

  // Filter functions
  const applyFiltersToMatchweeks = (matchweeks) => {
    return matchweeks.filter(matchweek => {
      // Country filter
      if (selectedCountry !== 'all') {
        const league = Object.values(matchweekData).find(l => l.name === matchweek.league);
        if (league && league.country !== selectedCountry) return false;
      }

      // Level filter
      if (selectedLevel !== 'all') {
        const league = Object.values(matchweekData).find(l => l.name === matchweek.league);
        if (league && league.level !== selectedLevel) return false;
      }

      return true;
    });
  };

  const applyFiltersToMatches = (matches) => {
    return matches.filter(match => {
      // Country filter
      if (selectedCountry !== 'all' && match.country !== selectedCountry) return false;
      // Level filter
      if (selectedLevel !== 'all' && match.level !== selectedLevel) return false;
      return true;
    });
  };

  const applyFiltersToTournaments = (tournaments) => {
    return tournaments.filter(tournament => {
      // Country filter
      if (selectedCountry !== 'all' && tournament.country !== selectedCountry) return false;
      // Level filter
      if (selectedLevel !== 'all' && tournament.level !== selectedLevel) return false;
      return true;
    });
  };

  // Handle match predictions
  const handleMatchPrediction = (matchId, field, value) => {
    setMatchPredictions(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [field]: value
      }
    }));
  };

  // Handle score input for individual matches
  const handleScoreInput = (matchId, team, goals) => {
    const numGoals = parseInt(goals) || 0;
    setMatchPredictions(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        score: {
          ...prev[matchId]?.score,
          [team]: numGoals
        }
      }
    }));
  };

  // Handle score input for matchweek matches
  const handleMatchweekScoreInput = (matchId, team, goals) => {
    const numGoals = parseInt(goals) || 0;
    setMatchScores(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [team]: numGoals
      }
    }));
  };

  // Handle matchweek predictions
  const handleMatchweekPrediction = (matchId, prediction, match) => {
    // If the match must have a winner, don't allow 'X' (draw) selection
    if (match?.mustHaveWinner && prediction === 'X') {
      return;
    }

    setMatchweekPredictions(prev => ({
      ...prev,
      [matchId]: prediction
    }));
  };

  // Handle tournament predictions
  const handleTournamentPrediction = (tournamentId, category, stage, position, team) => {
    setTournamentPredictions(prev => ({
      ...prev,
      [tournamentId]: {
        ...prev[tournamentId],
        [category]: {
          ...prev[tournamentId]?.[category],
          [stage]: stage === 'quarterfinalists' || stage === 'semifinalists' 
            ? (() => {
                const current = prev[tournamentId]?.[category]?.[stage] || [];
                const newArray = [...current];
                newArray[position] = team;
                return newArray;
              })()
            : team
        }
      }
    }));
  };

  // Get available matchweeks
  const getAvailableMatchweeks = () => {
    if (selectedLeague === 'all') {
      const allMatchweeks = [];
      Object.entries(matchweekData).forEach(([leagueKey, league]) => {
        Object.entries(league.matchweeks).forEach(([weekNum, week]) => {
          allMatchweeks.push({
            key: `${leagueKey}-${weekNum}`,
            name: `${league.name} - ${week.name}`,
            league: leagueKey,
            week: weekNum
          });
        });
      });
      return [{ key: 'all', name: 'All Matchweeks' }, ...allMatchweeks];
    } else {
      const league = matchweekData[selectedLeague];
      if (!league) return [{ key: 'all', name: 'All Matchweeks' }];

      const matchweeks = Object.entries(league.matchweeks).map(([weekNum, week]) => ({
        key: `${selectedLeague}-${weekNum}`,
        name: week.name,
        league: selectedLeague,
        week: weekNum
      }));

      return [{ key: 'all', name: 'All Matchweeks' }, ...matchweeks];
    }
  };

  // Get available individual matches
  const getAvailableMatches = () => {
    const matches = [{ key: 'all', name: 'All Matches' }];
    individualMatchData.forEach(match => {
      matches.push({
        key: match.id.toString(),
        name: `${match.home} vs ${match.away}`,
        match: match
      });
    });
    return matches;
  };

  // Get available tournaments
  const getAvailableTournaments = () => {
    const tournaments = [{ key: 'all', name: 'All Tournaments' }];
    tournamentData.forEach(tournament => {
      tournaments.push({
        key: tournament.id.toString(),
        name: tournament.name,
        tournament: tournament
      });
    });
    return tournaments;
  };

  // Get matchweeks to show with filters applied
  const getMatchweeksToShow = () => {
    let matchweeks = [];

    if (selectedLeague === 'all' && selectedMatchweek === 'all') {
      Object.entries(matchweekData).forEach(([leagueKey, league]) => {
        Object.entries(league.matchweeks).forEach(([weekNum, week]) => {
          matchweeks.push({
            key: `${leagueKey}-${weekNum}`,
            league: league.name,
            matchweek: week.name,
            matches: week.matches,
            leagueKey,
            weekNum
          });
        });
      });
    } else if (selectedLeague !== 'all' && selectedMatchweek === 'all') {
      const league = matchweekData[selectedLeague];
      if (league) {
        matchweeks = Object.entries(league.matchweeks).map(([weekNum, week]) => ({
          key: `${selectedLeague}-${weekNum}`,
          league: league.name,
          matchweek: week.name,
          matches: week.matches,
          leagueKey: selectedLeague,
          weekNum
        }));
      }
    } else if (selectedMatchweek !== 'all') {
      const [leagueKey, weekNum] = selectedMatchweek.split('-');
      const league = matchweekData[leagueKey];
      const week = league?.matchweeks[weekNum];
      if (week) {
        matchweeks = [{
          key: selectedMatchweek,
          league: league.name,
          matchweek: week.name,
          matches: week.matches,
          leagueKey,
          weekNum
        }];
      }
    }

    return applyFiltersToMatchweeks(matchweeks);
  };

  // Get individual matches to show with filters applied
  const getMatchesToShow = () => {
    let matches = [];
    if (selectedMatch === 'all') {
      matches = individualMatchData;
    } else {
      const match = individualMatchData.find(m => m.id.toString() === selectedMatch);
      matches = match ? [match] : [];
    }

    return applyFiltersToMatches(matches);
  };

  // Get tournaments to show with filters applied
  const getTournamentsToShow = () => {
    let tournaments = [];
    if (selectedTournament === 'all') {
      tournaments = tournamentData;
    } else {
      const tournament = tournamentData.find(t => t.id.toString() === selectedTournament);
      tournaments = tournament ? [tournament] : [];
    }

    return applyFiltersToTournaments(tournaments);
  };

  // Check if match predictions are valid
  const isMatchPredictionValid = (matchId) => {
    const prediction = matchweekPredictions[matchId];
    const scores = matchScores[matchId] || {};

    const match = Object.values(matchweekData)
      .flatMap(league => Object.values(league.matchweeks))
      .flatMap(week => week.matches)
      .find(m => m.id === matchId);

    if (!match) return true;

    // For matches that must have a winner
    if (match.mustHaveWinner) {
      // If using score input (for matchweeks with 8 or fewer matches)
      if (scores.home !== undefined && scores.away !== undefined) {
        // If the scores are equal, it's only valid if we note it's a penalty win
        if (scores.home === scores.away) {
          return prediction === '1' || prediction === '2'; // Penalty shootout
        }
        // Otherwise the score must match the prediction
        if (prediction === '1' && scores.home <= scores.away) return false;
        if (prediction === '2' && scores.home >= scores.away) return false;
      }
      // No draw allowed for matches that must have a winner
      return prediction !== 'X';
    }

    // For regular matches
    if (scores.home !== undefined && scores.away !== undefined) {
      // Score must match prediction
      if (prediction === '1' && scores.home <= scores.away) return false;
      if (prediction === 'X' && scores.home !== scores.away) return false;
      if (prediction === '2' && scores.home >= scores.away) return false;
    }

    return true;
  };

  // Handle stake matchweek prediction
  const handleStakeMatchweek = (matchweekData) => {
    const matchweekPredictionsToStake = {};
    const matchScoresToStake = {};
    let totalMatches = 0;
    let predictedMatches = 0;
    let allPredictionsValid = true;

    matchweekData.matches.forEach(match => {
      totalMatches++;
      if (matchweekPredictions[match.id]) {
        // Check if prediction is valid before staking
        if (!isMatchPredictionValid(match.id)) {
          allPredictionsValid = false;
          return;
        }

        matchweekPredictionsToStake[match.id] = matchweekPredictions[match.id];
        if (matchScores[match.id]) {
          matchScoresToStake[match.id] = matchScores[match.id];
        }
        predictedMatches++;
      }
    });

    if (predictedMatches === totalMatches && allPredictionsValid) {
      onStake({
        type: 'football-matchweek',
        league: matchweekData.league,
        matchweek: matchweekData.matchweek,
        predictions: matchweekPredictionsToStake,
        scores: matchScoresToStake,
        matches: matchweekData.matches,
        totalMatches: totalMatches,
        sport: 'Football'
      });
    }
  };

  // Handle stake individual match prediction
  const handleStakeMatch = (match) => {
    const prediction = matchPredictions[match.id];
    if (isMatchPredictionComplete(match)) {
      onStake({
        type: 'football-match',
        match: match,
        prediction: prediction,
        sport: 'Football'
      });
    }
  };

  // Handle stake tournament prediction
  const handleStakeTournament = (tournament, category) => {
    if (isTournamentCategoryComplete(tournament, category)) {
      onStake({
        type: 'football-tournament',
        tournament: tournament,
        category: category,
        predictions: tournamentPredictions[tournament.id]?.[category],
        sport: 'Football'
      });
    }
  };

  // Enhanced match prediction completion check
  const isMatchPredictionComplete = (match) => {
    const prediction = matchPredictions[match.id];
    if (!prediction) return false;

    if (match.drawAllowed) {
      if (!prediction.result) return false;
      if (!prediction.score || prediction.score.home === undefined || prediction.score.away === undefined) return false;
      return true;
    } else {
      if (!prediction.result || prediction.result === 'draw') return false;
      if (!prediction.structure) return false;
      if (!prediction.score || prediction.score.home === undefined || prediction.score.away === undefined) return false;

      if (prediction.structure === 'penalties') {
        if (prediction.score.home !== prediction.score.away) return false;
      }

      return true;
    }
  };

  // Check if tournament category is complete
  const isTournamentCategoryComplete = (tournament, category) => {
    const predictions = tournamentPredictions[tournament.id] || {};
    const categoryPredictions = predictions[category];

    if (!categoryPredictions) return false;

    const hasWinner = categoryPredictions.winner;
    const hasFinalist = categoryPredictions.finalist;
    const hasSemifinalists = categoryPredictions.semifinalists && 
      categoryPredictions.semifinalists.length === 2 && 
      categoryPredictions.semifinalists.every(p => p);
    const hasQuarterfinalists = categoryPredictions.quarterfinalists && 
      categoryPredictions.quarterfinalists.length === 4 && 
      categoryPredictions.quarterfinalists.every(p => p);

    return hasWinner && hasFinalist && hasSemifinalists && hasQuarterfinalists;
  };

  // Get available players for semifinalists with proper filtering
  const getAvailablePlayersForSemifinalists = (tournament, category, position) => {
    const teams = tournament.teams[category] || [];
    const categoryPredictions = tournamentPredictions[tournament.id]?.[category] || {};

    const excludedPlayers = new Set([
      categoryPredictions.winner,
      categoryPredictions.finalist,
      ...(categoryPredictions.semifinalists || []).filter((player, idx) => idx !== position && player)
    ].filter(Boolean));

    return teams.filter(player => !excludedPlayers.has(player));
  };

  // Get available players for quarterfinalists with proper filtering
  const getAvailablePlayersForQuarterfinalists = (tournament, category, position) => {
    const teams = tournament.teams[category] || [];
    const categoryPredictions = tournamentPredictions[tournament.id]?.[category] || {};

    const excludedPlayers = new Set([
      categoryPredictions.winner,
      categoryPredictions.finalist,
      ...(categoryPredictions.semifinalists || []).filter(player => player),
      ...(categoryPredictions.quarterfinalists || []).filter((player, idx) => idx !== position && player)
    ].filter(Boolean));

    return teams.filter(player => !excludedPlayers.has(player));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const truncateTeam = (teamName) => {
    const shortNames = {
      'Manchester City': 'Man City',
      'Manchester United': 'Man Utd',
      'West Ham United': 'West Ham',
      'Newcastle United': 'Newcastle',
      'Nottingham Forest': 'Nott Forest',
      'Crystal Palace': 'Crystal P.',
      'Sheffield United': 'Sheffield',
      'Wolverhampton': 'Wolves',
      'Real Madrid': 'Real M.',
      'Atletico Madrid': 'Atletico',
      'Athletic Bilbao': 'Athletic',
      'Real Sociedad': 'Real Soc.',
      'Real Mallorca': 'Mallorca',
      'Rayo Vallecano': 'Rayo'
    };
    return shortNames[teamName] || teamName;
  };

  const getButtonStyles = (option, isSelected) => {
    if (isSelected) {
      if (option === 'X') {
        return 'bg-bull-yellow border-bull-yellow text-bull-black shadow-lg transform scale-105';
      } else {
        return 'bg-bull-red border-bull-red text-bull-white shadow-lg transform scale-105';
      }
    }
    return 'bg-bull-charcoal border-bull-light-gray text-bull-light-gray hover:bg-bull-gray hover:border-bull-red hover:text-bull-white hover:shadow-md';
  };

  const matchweeksToShow = getMatchweeksToShow();
  const matchesToShow = getMatchesToShow();
  const tournamentsToShow = getTournamentsToShow();

  // Render the appropriate content based on prediction mode
  const renderContent = () => {
    switch (predictionMode) {
      case 'matchweek':
        return renderMatchweekMode();
      case 'match':
        return renderIndividualMatchMode();
      case 'tournament':
        return renderTournamentMode();
      default:
        return renderMatchweekMode();
    }
  };

  // Enhanced filter dropdown component
  const FilterDropdown = ({ isOpen, setIsOpen, options, selected, onSelect, icon, placeholder, className = "" }) => (
    <div className={`relative ${className}`}>
      <motion.button
        className="w-full bg-bull-charcoal border border-bull-light-gray rounded-bull px-3 py-2 flex items-center justify-between text-white hover:border-bull-red transition-all duration-300"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex items-center space-x-2 min-w-0">
          <SafeIcon icon={icon} className="w-3 h-3 text-bull-orange flex-shrink-0" />
          <span className="font-medium text-xs truncate">
            {options.find(o => o.key === selected)?.name || placeholder}
          </span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <SafeIcon icon={FiChevronDown} className="w-3 h-3 text-bull-light-gray" />
        </motion.div>
      </motion.button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute top-full left-0 right-0 mt-1 bg-bull-charcoal border border-bull-light-gray rounded-bull shadow-bull-lg z-50 max-h-40 overflow-y-auto"
        >
          {options.map((option) => (
            <motion.button
              key={option.key}
              className="w-full px-3 py-2 text-left hover:bg-bull-gray transition-colors border-b border-bull-gray last:border-b-0"
              onClick={() => {
                onSelect(option.key);
                setIsOpen(false);
              }}
              whileHover={{ backgroundColor: '#2A3132' }}
            >
              <div className="font-medium text-white text-xs">{option.name}</div>
            </motion.button>
          ))}
        </motion.div>
      )}
    </div>
  );

  // Render matchweek prediction mode
  const renderMatchweekMode = () => {
    return (
      <>
        {/* Enhanced Filters Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
          <FilterDropdown
            isOpen={isLeagueDropdownOpen}
            setIsOpen={setIsLeagueDropdownOpen}
            options={leagues}
            selected={selectedLeague}
            onSelect={setSelectedLeague}
            icon={FiCalendar}
            placeholder="All Competitions"
          />
          <FilterDropdown
            isOpen={isCountryDropdownOpen}
            setIsOpen={setIsCountryDropdownOpen}
            options={countryOptions}
            selected={selectedCountry}
            onSelect={setSelectedCountry}
            icon={FiMapPin}
            placeholder="All Countries"
          />
          <FilterDropdown
            isOpen={isLevelDropdownOpen}
            setIsOpen={setIsLevelDropdownOpen}
            options={levelOptions}
            selected={selectedLevel}
            onSelect={setSelectedLevel}
            icon={FiTarget}
            placeholder="All Levels"
          />
        </div>

        {/* Secondary Filters */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <FilterDropdown
            isOpen={isMatchweekDropdownOpen}
            setIsOpen={setIsMatchweekDropdownOpen}
            options={getAvailableMatchweeks()}
            selected={selectedMatchweek}
            onSelect={setSelectedMatchweek}
            icon={FiCalendar}
            placeholder="All Matchweeks"
          />
          {/* Clear Filters Button */}
          <motion.button
            className="bg-bull-red/20 border border-bull-red/40 rounded-bull px-3 py-2 flex items-center justify-center gap-2 text-bull-red hover:bg-bull-red/30 transition-all duration-300"
            onClick={clearAllFilters}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiX} className="w-3 h-3" />
            <span className="text-xs font-medium">Clear Filters</span>
          </motion.button>
        </div>

        {/* Matchweek Cards */}
        <div className="space-y-6">
          {matchweeksToShow.map((matchweekData, index) => {
            const totalMatches = matchweekData.matches.length;
            const predictedMatches = matchweekData.matches.filter(match => matchweekPredictions[match.id]).length;
            const allPredicted = predictedMatches === totalMatches;
            const hasValidPredictions = matchweekData.matches.every(match => 
              !matchweekPredictions[match.id] || isMatchPredictionValid(match.id)
            );
            const showScoreInput = matchweekData.matches.length <= 8;

            return (
              <motion.div
                key={matchweekData.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-bull-charcoal rounded-bull p-4 shadow-bull border border-bull-gray hover:border-bull-red/30 transition-all duration-300"
                whileHover={{ scale: 1.01 }}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-bold text-bull-red text-lg">
                      {matchweekData.league}
                    </h3>
                    <p className="text-bull-light-gray text-sm">{matchweekData.matchweek}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-bull-yellow font-bold text-lg">
                      {predictedMatches}/{totalMatches}
                    </div>
                    <div className="text-bull-light-gray text-xs">PREDICTED</div>
                  </div>
                </div>

                {/* Enhanced Matches Grid with Compact Mobile Design */}
                <div className="space-y-1.5 sm:space-y-2 mb-4">
                  {matchweekData.matches.map((match, matchIndex) => {
                    const prediction = matchweekPredictions[match.id];
                    const scores = matchScores[match.id] || {};
                    const isPredictionValid = isMatchPredictionValid(match.id);
                    const showPenaltyNote = match.mustHaveWinner && prediction && 
                      scores.home !== undefined && scores.away !== undefined && 
                      scores.home === scores.away;
                    const validationMessage = !isPredictionValid ? "Score doesn't match prediction" : "";

                    return (
                      <motion.div
                        key={match.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: matchIndex * 0.02 }}
                        className={`bg-bull-gray rounded-bull p-2 sm:p-3 border hover:border-bull-red/20 transition-all duration-300 relative ${
                          !isPredictionValid ? 'border-bull-red' : 
                          prediction ? 'border-bull-yellow/30' : 'border-bull-charcoal'
                        }`}
                      >
                        {/* Compact Mobile Layout (sm and below) */}
                        <div className="block sm:hidden">
                          {/* Match number indicator */}
                          <div className="absolute top-1 left-1">
                            <div className="w-4 h-4 bg-bull-red rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold leading-none">
                                {matchIndex + 1}
                              </span>
                            </div>
                          </div>

                          {/* Row 1: Teams */}
                          <div className="flex items-center justify-center mb-1.5 mt-1">
                            <div className="flex items-center justify-between w-full max-w-xs mx-auto">
                              <div className="text-bull-white font-medium text-xs text-center flex-1 truncate flex items-center justify-center gap-1">
                                {getTeamLogo(match.home)}
                                <span>{truncateTeam(match.home)}</span>
                              </div>
                              <div className="text-bull-light-gray text-xs mx-2 flex-shrink-0">
                                vs
                              </div>
                              <div className="text-bull-white font-medium text-xs text-center flex-1 truncate flex items-center justify-center gap-1">
                                {getTeamLogo(match.away)}
                                <span>{truncateTeam(match.away)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Notes and validation messages - moved to left side */}
                          <div className="flex items-center justify-start gap-2 mb-1 px-1 min-h-5">
                            {showPenaltyNote && (
                              <span className="text-xs text-bull-yellow">Penalties</span>
                            )}
                            {!isPredictionValid && (
                              <span className="text-xs text-bull-red">{validationMessage}</span>
                            )}
                          </div>

                          {/* Date & Time */}
                          <div className="text-center mb-2">
                            <div className="text-bull-light-gray text-xs">
                              {formatDate(match.date)} • {match.time}
                            </div>
                          </div>

                          {/* 1 X 2 Buttons - Made equal size */}
                          <div className="flex justify-center">
                            {['1', ...(match.mustHaveWinner ? [] : ['X']), '2'].map((option) => {
                              const isSelected = matchweekPredictions[match.id] === option;
                              const buttonCount = match.mustHaveWinner ? 2 : 3;
                              const buttonWidth = `${100 / buttonCount}%`;

                              return (
                                <motion.button
                                  key={option}
                                  className={`flex-1 py-1.5 rounded-bull text-xs font-bold border transition-all duration-300 mx-1 ${getButtonStyles(option, isSelected)}`}
                                  style={{ width: buttonWidth, maxWidth: buttonWidth }}
                                  onClick={() => handleMatchweekPrediction(match.id, option, match)}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  initial={{ scale: 1 }}
                                  animate={{ scale: isSelected ? 1.05 : 1 }}
                                >
                                  {option}
                                </motion.button>
                              );
                            })}
                          </div>

                          {/* Score inputs for matches with 8 or fewer games */}
                          {showScoreInput && prediction && (
                            <div className="mt-3 flex items-center justify-center">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  min="0"
                                  max="99"
                                  value={scores.home || ''}
                                  onChange={(e) => handleMatchweekScoreInput(match.id, 'home', e.target.value)}
                                  className={`w-10 h-8 text-center text-sm font-bold bg-bull-charcoal border rounded text-bull-white focus:outline-none ${
                                    !isPredictionValid ? 'border-bull-red' : 'border-bull-light-gray focus:border-bull-red'
                                  }`}
                                  placeholder="0"
                                />
                                <span className="text-bull-light-gray">-</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="99"
                                  value={scores.away || ''}
                                  onChange={(e) => handleMatchweekScoreInput(match.id, 'away', e.target.value)}
                                  className={`w-10 h-8 text-center text-sm font-bold bg-bull-charcoal border rounded text-bull-white focus:outline-none ${
                                    !isPredictionValid ? 'border-bull-red' : 'border-bull-light-gray focus:border-bull-red'
                                  }`}
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Desktop Layout (sm and above) - With added team logos */}
                        <div className="hidden sm:block">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <div className="text-bull-light-gray text-xs font-bold w-5 flex-shrink-0">
                                {matchIndex + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-bull-white font-medium text-sm flex items-center gap-1.5">
                                  {getTeamLogo(match.home)}
                                  <span>{match.home}</span>
                                  <span className="text-bull-light-gray mx-1">v</span>
                                  {getTeamLogo(match.away)}
                                  <span>{match.away}</span>
                                </div>
                                
                                {/* Notes and validation messages - moved to left side */}
                                <div className="flex items-center gap-2">
                                  {showPenaltyNote && (
                                    <span className="text-xs text-bull-yellow">Penalties</span>
                                  )}
                                  {!isPredictionValid && (
                                    <span className="text-xs text-bull-red">{validationMessage}</span>
                                  )}
                                </div>
                                
                                <div className="text-bull-light-gray text-xs">
                                  {formatDate(match.date)} • {match.time}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2 flex-shrink-0">
                              {/* Equal-sized buttons */}
                              <div className="flex">
                                {['1', ...(match.mustHaveWinner ? [] : ['X']), '2'].map((option) => {
                                  const isSelected = matchweekPredictions[match.id] === option;

                                  return (
                                    <motion.button
                                      key={option}
                                      className={`w-12 py-2 rounded-bull text-xs font-bold border-2 transition-all duration-300 mx-1 ${getButtonStyles(option, isSelected)}`}
                                      onClick={() => handleMatchweekPrediction(match.id, option, match)}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      initial={{ scale: 1 }}
                                      animate={{ scale: isSelected ? 1.05 : 1 }}
                                    >
                                      {option}
                                    </motion.button>
                                  );
                                })}
                              </div>

                              {/* Score inputs for matches with 8 or fewer games */}
                              {showScoreInput && prediction && (
                                <div className="ml-4 flex items-center space-x-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max="99"
                                    value={scores.home || ''}
                                    onChange={(e) => handleMatchweekScoreInput(match.id, 'home', e.target.value)}
                                    className={`w-12 h-8 text-center text-sm font-bold bg-bull-charcoal border rounded text-bull-white focus:outline-none ${
                                      !isPredictionValid ? 'border-bull-red' : 'border-bull-light-gray focus:border-bull-red'
                                    }`}
                                    placeholder="0"
                                  />
                                  <span className="text-bull-light-gray">-</span>
                                  <input
                                    type="number"
                                    min="0"
                                    max="99"
                                    value={scores.away || ''}
                                    onChange={(e) => handleMatchweekScoreInput(match.id, 'away', e.target.value)}
                                    className={`w-12 h-8 text-center text-sm font-bold bg-bull-charcoal border rounded text-bull-white focus:outline-none ${
                                      !isPredictionValid ? 'border-bull-red' : 'border-bull-light-gray focus:border-bull-red'
                                    }`}
                                    placeholder="0"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-bull-light-gray">Progress</span>
                    <span className="text-bull-yellow font-bold">
                      {Math.round((predictedMatches / totalMatches) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-bull-gray rounded-full h-2">
                    <motion.div
                      className="bg-gradient-to-r from-bull-red to-bull-orange h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${(predictedMatches / totalMatches) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>

                {/* Validation Warning */}
                {!hasValidPredictions && (
                  <div className="mb-4 p-3 bg-bull-red/10 border border-bull-red rounded-bull">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-bull-red flex-shrink-0" />
                      <p className="text-bull-red text-sm">
                        Some predictions don't match the scores. Please check and correct them.
                      </p>
                    </div>
                  </div>
                )}

                {/* Stake Button */}
                <motion.button
                  className={`w-full py-3 rounded-bull font-bold text-sm transition-all duration-300 ${
                    allPredicted && hasValidPredictions
                      ? 'bg-gradient-to-r from-bull-red to-bull-red-light hover:from-bull-red-light hover:to-bull-red text-bull-white'
                      : 'bg-bull-gray text-bull-light-gray cursor-not-allowed'
                  }`}
                  onClick={() => handleStakeMatchweek(matchweekData)}
                  disabled={!allPredicted || !hasValidPredictions}
                  whileHover={allPredicted && hasValidPredictions ? { scale: 1.02 } : {}}
                  whileTap={allPredicted && hasValidPredictions ? { scale: 0.98 } : {}}
                >
                  {!allPredicted 
                    ? `${totalMatches - predictedMatches} LEFT`
                    : !hasValidPredictions 
                      ? 'FIX PREDICTION ERRORS' 
                      : 'STAKE PREDICTION'
                  }
                </motion.button>
              </motion.div>
            );
          })}
        </div>

        {matchweeksToShow.length === 0 && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⚽</div>
            <h3 className="text-xl font-bold text-bull-white mb-2">No Matches Available</h3>
            <p className="text-bull-light-gray">No matchweeks available for the selected filters</p>
            <motion.button
              className="mt-4 bg-bull-red text-bull-white px-6 py-2 rounded-bull font-medium"
              onClick={clearAllFilters}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Clear All Filters
            </motion.button>
          </div>
        )}
      </>
    );
  };

  // Render individual match prediction mode
  const renderIndividualMatchMode = () => {
    return (
      <>
        {/* Enhanced Filters for Individual Matches */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
          <FilterDropdown
            isOpen={isMatchDropdownOpen}
            setIsOpen={setIsMatchDropdownOpen}
            options={getAvailableMatches()}
            selected={selectedMatch}
            onSelect={setSelectedMatch}
            icon={FiTarget}
            placeholder="All Matches"
          />
          <FilterDropdown
            isOpen={isCountryDropdownOpen}
            setIsOpen={setIsCountryDropdownOpen}
            options={countryOptions}
            selected={selectedCountry}
            onSelect={setSelectedCountry}
            icon={FiMapPin}
            placeholder="All Countries"
          />
          <FilterDropdown
            isOpen={isLevelDropdownOpen}
            setIsOpen={setIsLevelDropdownOpen}
            options={levelOptions}
            selected={selectedLevel}
            onSelect={setSelectedLevel}
            icon={FiTarget}
            placeholder="All Levels"
          />
          <motion.button
            className="bg-bull-red/20 border border-bull-red/40 rounded-bull px-3 py-2 flex items-center justify-center gap-2 text-bull-red hover:bg-bull-red/30 transition-all duration-300"
            onClick={clearAllFilters}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiX} className="w-3 h-3" />
            <span className="text-xs font-medium">Clear</span>
          </motion.button>
        </div>

        {/* Individual Match Cards */}
        <div className="space-y-6">
          {matchesToShow.map((match, index) => {
            const prediction = matchPredictions[match.id] || {};
            const isComplete = isMatchPredictionComplete(match);

            return (
              <motion.div
                key={match.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-bull-charcoal rounded-bull p-4 shadow-bull border border-bull-gray hover:border-bull-red/30 transition-all duration-300"
                whileHover={{ scale: 1.01 }}
              >
                {/* Improved Mobile-Responsive Match Header */}
                <div className="text-center mb-4">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-bull-red to-bull-yellow px-3 py-1 rounded text-bull-white font-bold text-xs mb-2">
                    <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                    {match.competition} • {match.stage}
                  </div>

                  {/* Better Mobile-First Match Info Layout */}
                  <div className="space-y-2">
                    {/* Match date and venue with proper wrapping */}
                    <div className="flex flex-wrap justify-center gap-2 text-bull-light-gray text-xs">
                      <span className="px-2 py-1 bg-bull-gray rounded inline-block">{match.date} • {match.time}</span>
                      <span className="px-2 py-1 bg-bull-gray rounded inline-block max-w-full truncate">{match.venue}</span>
                    </div>

                    {/* Match status indicators with better spacing */}
                    <div className="flex flex-wrap justify-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium inline-block ${
                        match.drawAllowed 
                          ? 'bg-bull-yellow/20 text-bull-yellow' 
                          : 'bg-bull-red/20 text-bull-red'
                      }`}>
                        {match.drawAllowed ? 'Draw Allowed' : 'Must Have Winner'}
                      </span>
                    </div>
                  </div>

                  {/* Fixed Teams Display with Better Alignment */}
                  <div className="flex items-center justify-center my-4">
                    <div className="flex items-center max-w-full">
                      {/* Home Team */}
                      <div className="flex flex-col items-center w-[40%]">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-2">
                          {getTeamLogo(match.home)}
                        </div>
                        <h3 className="font-heading font-bold text-bull-white text-sm sm:text-xl leading-tight px-1 truncate max-w-full">
                          {truncateTeam(match.home)}
                        </h3>
                      </div>

                      {/* VS Separator - Fixed Width */}
                      <div className="text-bull-red text-xl sm:text-2xl font-bold mx-2 sm:mx-4 w-[20%] text-center flex-shrink-0">
                        VS
                      </div>

                      {/* Away Team */}
                      <div className="flex flex-col items-center w-[40%]">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-2">
                          {getTeamLogo(match.away)}
                        </div>
                        <h3 className="font-heading font-bold text-bull-white text-sm sm:text-xl leading-tight px-1 truncate max-w-full">
                          {truncateTeam(match.away)}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Improved Mobile-Responsive Prediction Steps */}
                <div className="space-y-4">
                  {/* Step 1: Match Result */}
                  <div className="bg-bull-gray rounded-bull p-3 sm:p-4">
                    <h4 className="text-bull-yellow font-bold text-sm mb-3 text-center">
                      Match Result
                    </h4>
                    <div className={`grid gap-2 ${match.drawAllowed ? 'grid-cols-3' : 'grid-cols-2'}`}>
                      {[match.home, ...(match.drawAllowed ? ['draw'] : []), match.away].map((option) => {
                        const isSelected = prediction.result === option;
                        return (
                          <motion.button
                            key={option}
                            className={`p-2 rounded-bull border-2 transition-all duration-300 ${
                              isSelected
                                ? option === 'draw'
                                  ? 'border-bull-yellow bg-bull-yellow/20 text-bull-yellow'
                                  : 'border-bull-red bg-bull-red/20 text-bull-red'
                                : 'border-bull-light-gray text-bull-light-gray hover:border-bull-red hover:text-bull-white hover:bg-bull-red/10'
                            }`}
                            onClick={() => handleMatchPrediction(match.id, 'result', option)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className="text-xs font-bold text-center leading-tight">
                              {option === 'draw' ? 'DRAW' : truncateTeam(option)}
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Step 2: Match Structure (for no-draw matches) */}
                  <AnimatePresence>
                    {prediction.result && !match.drawAllowed && prediction.result !== 'draw' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-bull-gray rounded-bull p-3 sm:p-4"
                      >
                        <h4 className="text-bull-yellow font-bold text-sm mb-3 text-center">
                          Match Structure
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {getMatchStructureOptions(match).map((structure) => {
                            const isSelected = prediction.structure === structure.key;
                            return (
                              <motion.button
                                key={structure.key}
                                className={`p-2 rounded-bull border transition-all duration-300 ${
                                  isSelected
                                    ? 'border-bull-red bg-bull-red/20 text-bull-red'
                                    : 'border-bull-light-gray text-bull-light-gray hover:border-bull-red hover:text-bull-white hover:bg-bull-red/10'
                                }`}
                                onClick={() => handleMatchPrediction(match.id, 'structure', structure.key)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <div className="text-xs font-bold text-center mb-1">
                                  {structure.label}
                                </div>
                                <div className="text-xs opacity-70 text-center leading-tight">
                                  {structure.description}
                                </div>
                              </motion.button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Step 3: Improved Mobile-Responsive Score Input */}
                  <AnimatePresence>
                    {prediction.result && (match.drawAllowed || prediction.structure) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-bull-gray rounded-bull p-3 sm:p-4"
                      >
                        <h4 className="text-bull-yellow font-bold text-sm mb-3 text-center">
                          Final Score
                        </h4>

                        {/* Score validation warning for penalties */}
                        {prediction.structure === 'penalties' && (
                          <div className="mb-3 p-2 bg-bull-red/10 border border-bull-red/20 rounded text-center">
                            <div className="flex items-center justify-center gap-2 text-bull-red text-xs">
                              <SafeIcon icon={FiAlertTriangle} className="w-3 h-3" />
                              <span className="leading-tight">Penalties require a draw score (same goals for both teams)</span>
                            </div>
                          </div>
                        )}

                        {/* Improved Mobile-First Score Layout */}
                        <div className="flex items-center justify-center">
                          <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
                            {/* Home Team */}
                            <div className="text-center">
                              <div className="text-bull-white font-medium text-xs mb-1 truncate">
                                {truncateTeam(match.home)}
                              </div>
                              <input
                                type="number"
                                min="0"
                                max="20"
                                value={prediction.score?.home || ''}
                                onChange={(e) => handleScoreInput(match.id, 'home', e.target.value)}
                                className="w-full h-12 text-center text-xl font-bold bg-bull-charcoal border-2 border-bull-light-gray rounded-bull text-bull-white focus:border-bull-red focus:outline-none transition-colors"
                                placeholder="0"
                              />
                            </div>

                            {/* VS Separator */}
                            <div className="flex items-center justify-center">
                              <div className="text-center text-bull-light-gray text-lg font-bold">
                                -
                              </div>
                            </div>

                            {/* Away Team */}
                            <div className="text-center">
                              <div className="text-bull-white font-medium text-xs mb-1 truncate">
                                {truncateTeam(match.away)}
                              </div>
                              <input
                                type="number"
                                min="0"
                                max="20"
                                value={prediction.score?.away || ''}
                                onChange={(e) => handleScoreInput(match.id, 'away', e.target.value)}
                                className="w-full h-12 text-center text-xl font-bold bg-bull-charcoal border-2 border-bull-light-gray rounded-bull text-bull-white focus:border-bull-red focus:outline-none transition-colors"
                                placeholder="0"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Score validation */}
                        {prediction.score && prediction.structure === 'penalties' && 
                         prediction.score.home !== prediction.score.away && (
                          <div className="mt-3 text-center text-bull-red text-xs">
                            Score must be equal for penalty shootout
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Prediction Status */}
                  <div className="bg-bull-charcoal rounded-bull p-3 border border-bull-gray">
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-bull-light-gray">Prediction Status</span>
                      <span className="text-bull-yellow font-bold">
                        {isComplete ? 'Complete' : 'In Progress'}
                      </span>
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className={`flex items-center gap-2 ${prediction.result ? 'text-bull-yellow' : 'text-bull-light-gray'}`}>
                        <span>{prediction.result ? '✓' : '○'}</span>
                        <span>Match Result Selected</span>
                      </div>
                      {!match.drawAllowed && (
                        <div className={`flex items-center gap-2 ${prediction.structure ? 'text-bull-yellow' : 'text-bull-light-gray'}`}>
                          <span>{prediction.structure ? '✓' : '○'}</span>
                          <span>Match Structure Selected</span>
                        </div>
                      )}
                      <div className={`flex items-center gap-2 ${
                        prediction.score && prediction.score.home !== undefined && prediction.score.away !== undefined 
                          ? 'text-bull-yellow' : 'text-bull-light-gray'
                      }`}>
                        <span>{prediction.score && prediction.score.home !== undefined && prediction.score.away !== undefined ? '✓' : '○'}</span>
                        <span>Final Score Entered</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stake Button */}
                <motion.button
                  className={`w-full py-3 rounded-bull font-bold text-sm transition-all duration-300 mt-4 ${
                    isComplete
                      ? 'bg-gradient-to-r from-bull-red to-bull-red-light hover:from-bull-red-light hover:to-bull-red text-bull-white'
                      : 'bg-bull-gray text-bull-light-gray cursor-not-allowed'
                  }`}
                  onClick={() => handleStakeMatch(match)}
                  disabled={!isComplete}
                  whileHover={isComplete ? { scale: 1.02 } : {}}
                  whileTap={isComplete ? { scale: 0.98 } : {}}
                >
                  {isComplete ? 'STAKE MATCH PREDICTION' : 'COMPLETE ALL PREDICTION STEPS'}
                </motion.button>
              </motion.div>
            );
          })}
        </div>

        {matchesToShow.length === 0 && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⚽</div>
            <h3 className="text-xl font-bold text-bull-white mb-2">No Matches Available</h3>
            <p className="text-bull-light-gray">No matches available for the selected filters</p>
            <motion.button
              className="mt-4 bg-bull-red text-bull-white px-6 py-2 rounded-bull font-medium"
              onClick={clearAllFilters}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Clear All Filters
            </motion.button>
          </div>
        )}
      </>
    );
  };

  // Render tournament prediction mode
  const renderTournamentMode = () => {
    return (
      <>
        {/* Enhanced Filters for Tournaments */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
          <FilterDropdown
            isOpen={isTournamentDropdownOpen}
            setIsOpen={setIsTournamentDropdownOpen}
            options={getAvailableTournaments()}
            selected={selectedTournament}
            onSelect={setSelectedTournament}
            icon={FiTrophy}
            placeholder="All Tournaments"
          />
          <FilterDropdown
            isOpen={isCountryDropdownOpen}
            setIsOpen={setIsCountryDropdownOpen}
            options={countryOptions}
            selected={selectedCountry}
            onSelect={setSelectedCountry}
            icon={FiMapPin}
            placeholder="All Countries"
          />
          <FilterDropdown
            isOpen={isLevelDropdownOpen}
            setIsOpen={setIsLevelDropdownOpen}
            options={levelOptions}
            selected={selectedLevel}
            onSelect={setSelectedLevel}
            icon={FiTarget}
            placeholder="All Levels"
          />
          <motion.button
            className="bg-bull-red/20 border border-bull-red/40 rounded-bull px-3 py-2 flex items-center justify-center gap-2 text-bull-red hover:bg-bull-red/30 transition-all duration-300"
            onClick={clearAllFilters}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiX} className="w-3 h-3" />
            <span className="text-xs font-medium">Clear</span>
          </motion.button>
        </div>

        {/* Tournament Cards */}
        <div className="space-y-6">
          {tournamentsToShow.map((tournament, index) => (
            <motion.div
              key={tournament.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-bull-charcoal rounded-bull p-4 shadow-bull border border-bull-gray hover:border-bull-red/30 transition-all duration-300"
              whileHover={{ scale: 1.01 }}
            >
              {/* Tournament Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-bull-yellow to-bull-red px-3 py-1 rounded text-bull-black font-bold text-xs mb-2">
                  <SafeIcon icon={FiTrophy} className="w-3 h-3" />
                  Tournament Structure
                </div>
                <h3 className="font-heading font-bold text-bull-yellow text-xl mb-1">
                  {tournament.name}
                </h3>
                <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4 text-bull-light-gray text-sm">
                  <span>{tournament.date}</span>
                  <span>{tournament.location}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    tournament.level === 'International' ? 'bg-bull-red/20 text-bull-red' : 'bg-bull-yellow/20 text-bull-yellow'
                  }`}>
                    {tournament.level}
                  </span>
                </div>
              </div>

              {/* Separate Category Cards */}
              <div className="space-y-6">
                {tournament.categories.map((category) => {
                  const categoryPredictions = tournamentPredictions[tournament.id]?.[category] || {};
                  const teams = tournament.teams[category] || [];
                  const isComplete = isTournamentCategoryComplete(tournament, category);

                  return (
                    <motion.div
                      key={category}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2 }}
                      className="bg-gradient-to-br from-bull-gray/20 to-bull-charcoal/20 rounded-bull p-4 border border-bull-red/20"
                    >
                      {/* Category Header */}
                      <div className="text-center mb-4">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <SafeIcon icon={FiUsers} className="w-4 h-4 text-bull-red" />
                          <h4 className="text-bull-white font-bold text-base">
                            {category}
                          </h4>
                        </div>
                        <div className="text-xs text-bull-light-gray">
                          {teams.length} teams • 8 positions to predict
                        </div>
                      </div>

                      {/* Tournament Structure Grid */}
                      <div className="space-y-3">
                        {/* Winner */}
                        <div className="bg-bull-charcoal rounded p-3">
                          <label className="block text-bull-yellow font-bold text-xs sm:text-sm mb-2 text-center">
                            🏆 Champion
                          </label>
                          <select
                            className="w-full bg-bull-dark-black border border-bull-light-gray rounded px-3 py-2 text-white text-xs sm:text-sm focus:border-bull-yellow focus:outline-none"
                            value={categoryPredictions.winner || ''}
                            onChange={(e) => handleTournamentPrediction(tournament.id, category, 'winner', 0, e.target.value)}
                          >
                            <option value="">Select Champion</option>
                            {teams.map(team => (
                              <option key={team} value={team}>{team}</option>
                            ))}
                          </select>
                        </div>

                        {/* Finalist */}
                        <div className="bg-bull-charcoal rounded p-3">
                          <label className="block text-bull-red font-bold text-xs sm:text-sm mb-2 text-center">
                            🥈 Runner-up
                          </label>
                          <select
                            className="w-full bg-bull-dark-black border border-bull-light-gray rounded px-3 py-2 text-white text-xs sm:text-sm focus:border-bull-red focus:outline-none"
                            value={categoryPredictions.finalist || ''}
                            onChange={(e) => handleTournamentPrediction(tournament.id, category, 'finalist', 0, e.target.value)}
                          >
                            <option value="">Select Runner-up</option>
                            {teams.filter(p => p !== categoryPredictions.winner).map(team => (
                              <option key={team} value={team}>{team}</option>
                            ))}
                          </select>
                        </div>

                        {/* Semifinalists */}
                        <div className="bg-bull-charcoal rounded p-3">
                          <label className="block text-bull-light-gray font-bold text-xs sm:text-sm mb-2 text-center">
                            🥉 Semifinalists
                          </label>
                          <div className="grid grid-cols-1 gap-2">
                            {[0, 1].map((position) => (
                              <select
                                key={position}
                                className="w-full bg-bull-dark-black border border-bull-light-gray rounded px-3 py-2 text-white text-xs sm:text-sm focus:border-bull-light-gray focus:outline-none"
                                value={categoryPredictions.semifinalists?.[position] || ''}
                                onChange={(e) => handleTournamentPrediction(tournament.id, category, 'semifinalists', position, e.target.value)}
                              >
                                <option value="">Semifinalist {position + 1}</option>
                                {getAvailablePlayersForSemifinalists(tournament, category, position).map(team => (
                                  <option key={team} value={team}>{team}</option>
                                ))}
                              </select>
                            ))}
                          </div>
                        </div>

                        {/* Quarterfinalists */}
                        <div className="bg-bull-charcoal rounded p-3">
                          <label className="block text-bull-light-gray font-bold text-xs sm:text-sm mb-2 text-center">
                            🎯 Quarterfinalists
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {[0, 1, 2, 3].map((position) => (
                              <select
                                key={position}
                                className="w-full bg-bull-dark-black border border-bull-light-gray rounded px-2 sm:px-3 py-1.5 sm:py-2 text-white text-xs focus:border-bull-light-gray focus:outline-none"
                                value={categoryPredictions.quarterfinalists?.[position] || ''}
                                onChange={(e) => handleTournamentPrediction(tournament.id, category, 'quarterfinalists', position, e.target.value)}
                              >
                                <option value="">QF {position + 1}</option>
                                {getAvailablePlayersForQuarterfinalists(tournament, category, position).map(team => (
                                  <option key={team} value={team}>{team}</option>
                                ))}
                              </select>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Category Progress */}
                      <div className="mt-4">
                        <div className="flex justify-between text-xs mb-2">
                          <span className="text-bull-light-gray">Progress</span>
                          <span className="text-bull-yellow font-bold">
                            {[
                              categoryPredictions.winner,
                              categoryPredictions.finalist,
                              ...(categoryPredictions.semifinalists || []),
                              ...(categoryPredictions.quarterfinalists || [])
                            ].filter(Boolean).length}/8 Positions
                          </span>
                        </div>
                        <div className="w-full bg-bull-dark-black rounded-full h-1.5">
                          <motion.div
                            className="bg-gradient-to-r from-bull-yellow to-bull-red h-1.5 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${([
                              categoryPredictions.winner,
                              categoryPredictions.finalist,
                              ...(categoryPredictions.semifinalists || []),
                              ...(categoryPredictions.quarterfinalists || [])
                            ].filter(Boolean).length / 8) * 100}%` }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                      </div>

                      {/* Category Stake Button */}
                      <motion.button
                        className={`w-full mt-4 py-3 rounded-bull font-bold text-xs sm:text-sm transition-all duration-300 ${
                          isComplete
                            ? 'bg-gradient-to-r from-bull-yellow to-bull-red hover:from-bull-red hover:to-bull-yellow text-bull-black'
                            : 'bg-bull-gray text-bull-light-gray cursor-not-allowed'
                        }`}
                        onClick={() => handleStakeTournament(tournament, category)}
                        disabled={!isComplete}
                        whileHover={isComplete ? { scale: 1.02 } : {}}
                        whileTap={isComplete ? { scale: 0.98 } : {}}
                      >
                        {isComplete 
                          ? `STAKE ${category.toUpperCase()} PREDICTION` 
                          : `COMPLETE ${category.split("'s")[0].toUpperCase()}'S STRUCTURE`
                        }
                      </motion.button>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {tournamentsToShow.length === 0 && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🏆</div>
            <h3 className="text-xl font-bold text-bull-white mb-2">No Tournaments Available</h3>
            <p className="text-bull-light-gray">No tournaments available for the selected filters</p>
            <motion.button
              className="mt-4 bg-bull-red text-bull-white px-6 py-2 rounded-bull font-medium"
              onClick={clearAllFilters}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Clear All Filters
            </motion.button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Instructions */}
      <motion.div
        className="bg-bull-charcoal border border-bull-red/20 rounded-bull p-3 sm:p-4 shadow-bull"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiInfo} className="w-5 h-5 text-bull-yellow flex-shrink-0" />
          <div>
            <h3 className="font-heading text-sm font-bold text-bull-white mb-1">
              Football Predictions
            </h3>
            <p className="text-xs text-bull-light-gray">
              {predictionMode === 'matchweek' 
                ? 'Select 1 (Home), X (Draw), or 2 (Away) for each match. Use filters to find specific competitions or countries.'
                : predictionMode === 'match'
                  ? 'Predict winner/draw, match structure (if no draw allowed), and exact score. Filter by competition level, country, or match importance.'
                  : 'Predict tournament outcome including winner, finalist, semifinalists, and quarterfinalists. Filter by region and competition level.'
              }
            </p>
          </div>
        </div>
      </motion.div>

      {/* Prediction Mode Selector */}
      <div className="bg-bull-charcoal rounded-bull p-3 border border-bull-gray">
        <label className="block text-bull-light-gray text-xs sm:text-sm font-medium mb-2">Prediction Mode</label>
        <div className="grid grid-cols-3 gap-2">
          {predictionModes.map((mode) => (
            <motion.button
              key={mode.key}
              className={`p-2 sm:p-3 rounded text-xs font-medium transition-all duration-300 ${
                predictionMode === mode.key
                  ? 'bg-bull-yellow text-bull-black'
                  : 'bg-bull-gray text-bull-light-gray hover:bg-bull-light-gray hover:text-bull-white'
              }`}
              onClick={() => {
                setPredictionMode(mode.key);
                clearAllFilters();
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="font-bold">{mode.label}</div>
              <div className="text-xs opacity-70 hidden sm:block">{mode.description}</div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Render the appropriate content based on prediction mode */}
      {renderContent()}
    </div>
  );
};

export default FootballCard;